import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSocketStore } from '../stores/socketStore';
import { useAuthStore } from '../stores/authStore';
import { 
  Music, 
  Send, 
  Check, 
  Trophy,
  Zap,
  Clock,
  Users,
  Volume2,
  VolumeX,
  History,
  Sparkles,
  Skull
} from 'lucide-react';
import confetti from 'canvas-confetti';
import PowerUpsMenu from '../components/PowerUpsMenu';
import PowerUpEffects, { usePowerUpEffects } from '../components/PowerUpEffects';
import QuizMode from '../components/game/QuizMode';
import BattleRoyaleStatus from '../components/game/BattleRoyaleStatus';
import TeamScoreDisplay from '../components/game/TeamScoreDisplay';
import GameModeBadge from '../components/game/GameModeBadge';

// Convertir le slider (0-100) vers volume audio (0-1) avec courbe logarithmique
function sliderToVolume(sliderValue: number): number {
  // Courbe exponentielle pour une perception linéaire du volume
  // slider 0 = volume 0, slider 100 = volume 1
  if (sliderValue <= 0) return 0;
  if (sliderValue >= 100) return 1;
  // Utilise une courbe exponentielle: volume = (e^(x*3) - 1) / (e^3 - 1)
  const vol = (Math.exp(sliderValue / 100 * 3) - 1) / (Math.E ** 3 - 1);
  return Math.min(1, Math.max(0, vol)); // Clamp entre 0 et 1
}

// Hook pour le volume persistant
function useVolume() {
  // Stocke la valeur du slider (0-100) pour l'UI
  const [sliderValue, setSliderValue] = useState(() => {
    const saved = localStorage.getItem('blindtest-volume-slider');
    return saved ? parseFloat(saved) : 50; // 50% par défaut (environ 0.35 en volume réel)
  });
  
  const [isMuted, setIsMuted] = useState(() => {
    return localStorage.getItem('blindtest-muted') === 'true';
  });
  
  // Volume réel calculé à partir du slider
  const volume = sliderToVolume(sliderValue);
  
  const setVolume = useCallback((sliderVal: number) => {
    setSliderValue(sliderVal);
    localStorage.setItem('blindtest-volume-slider', String(sliderVal));
  }, []);
  
  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      localStorage.setItem('blindtest-muted', String(!prev));
      return !prev;
    });
  }, []);
  
  return { volume, sliderValue, setVolume, isMuted, toggleMute };
}

// Créer un contexte audio global (pour les sons de feedback uniquement)
let audioContext: AudioContext | null = null;

function getAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
}

// Hook pour la normalisation audio avec DynamicsCompressor
// Égalise le volume entre les différentes chansons
function useAudioNormalization(audioRef: React.RefObject<HTMLAudioElement>, volume: number, isMuted: boolean) {
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const compressorRef = useRef<DynamicsCompressorNode | null>(null);
  const isConnectedRef = useRef(false);
  
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const setupAudioPipeline = () => {
      if (isConnectedRef.current) return;
      
      try {
        const ctx = getAudioContext();
        if (ctx.state === 'suspended') {
          ctx.resume();
        }
        
        // Créer les nodes seulement une fois
        if (!sourceRef.current) {
          sourceRef.current = ctx.createMediaElementSource(audio);
        }
        
        if (!gainRef.current) {
          gainRef.current = ctx.createGain();
        }
        
        if (!compressorRef.current) {
          compressorRef.current = ctx.createDynamicsCompressor();
          // Configuration du compresseur pour normaliser le volume
          compressorRef.current.threshold.setValueAtTime(-24, ctx.currentTime); // Seuil bas pour capter les variations
          compressorRef.current.knee.setValueAtTime(30, ctx.currentTime);
          compressorRef.current.ratio.setValueAtTime(12, ctx.currentTime); // Ratio élevé pour forte compression
          compressorRef.current.attack.setValueAtTime(0.003, ctx.currentTime);
          compressorRef.current.release.setValueAtTime(0.25, ctx.currentTime);
        }
        
        // Connecter: Source -> Compressor -> Gain -> Destination
        sourceRef.current.connect(compressorRef.current);
        compressorRef.current.connect(gainRef.current);
        gainRef.current.connect(ctx.destination);
        
        isConnectedRef.current = true;
        console.log('🔊 Audio normalization pipeline connecté');
      } catch (e) {
        console.error('Erreur setup audio normalization:', e);
      }
    };
    
    // Setup au premier play
    const handlePlay = () => {
      setupAudioPipeline();
    };
    
    audio.addEventListener('play', handlePlay);
    
    return () => {
      audio.removeEventListener('play', handlePlay);
    };
  }, [audioRef]);
  
  // Mettre à jour le gain quand le volume change
  useEffect(() => {
    if (gainRef.current) {
      const ctx = getAudioContext();
      const targetGain = isMuted ? 0 : volume;
      gainRef.current.gain.setValueAtTime(targetGain, ctx.currentTime);
    }
  }, [volume, isMuted]);
}

// Hook pour les sons de feedback - accepte volume et isMuted en paramètres
function useSoundEffects(volume: number, isMuted: boolean) {
  
  const playSuccess = useCallback(() => {
    if (isMuted) return;
    
    try {
      const ctx = getAudioContext();
      if (ctx.state === 'suspended') ctx.resume();
      
      const now = ctx.currentTime;
      
      // Son de succès (arpège montant Do-Mi-Sol)
      [523.25, 659.25, 783.99].forEach((freq, i) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(freq, now + i * 0.1);
        gainNode.gain.setValueAtTime(volume * 0.3, now + i * 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.2);
        oscillator.start(now + i * 0.1);
        oscillator.stop(now + i * 0.1 + 0.2);
      });
    } catch (e) {
      console.error('Error playing success sound:', e);
    }
  }, [volume, isMuted]);
  
  const playError = useCallback(() => {
    if (isMuted) return;
    
    try {
      const ctx = getAudioContext();
      if (ctx.state === 'suspended') ctx.resume();
      
      const now = ctx.currentTime;
      
      // Son d'erreur (buzzer descendant)
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(200, now);
      oscillator.frequency.exponentialRampToValueAtTime(100, now + 0.2);
      gainNode.gain.setValueAtTime(volume * 0.15, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      oscillator.start(now);
      oscillator.stop(now + 0.3);
    } catch (e) {
      console.error('Error playing error sound:', e);
    }
  }, [volume, isMuted]);
  
  const playCountdown = useCallback(() => {
    if (isMuted) return;
    
    try {
      const ctx = getAudioContext();
      if (ctx.state === 'suspended') ctx.resume();
      
      const now = ctx.currentTime;
      
      // Son de countdown (bip court)
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, now);
      gainNode.gain.setValueAtTime(volume * 0.2, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
      oscillator.start(now);
      oscillator.stop(now + 0.1);
    } catch (e) {
      console.error('Error playing countdown sound:', e);
    }
  }, [volume, isMuted]);
  
  return { playSuccess, playError, playCountdown };
}

// Composant visualiseur audio avec manipulation DOM directe (60fps)
function AudioVisualizer({ audioRef }: { audioRef: React.RefObject<HTMLAudioElement> }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const barsRef = useRef<number[]>(new Array(20).fill(0.1));
  const isPlayingRef = useRef(false);
  const animationRef = useRef<number | null>(null);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const barElements = container.querySelectorAll<HTMLDivElement>('.visualizer-bar');
    
    const animate = () => {
      const isPlaying = isPlayingRef.current;
      const time = performance.now() / 1000;
      
      barsRef.current = barsRef.current.map((currentValue, i) => {
        if (!isPlaying) {
          return Math.max(0.08, currentValue * 0.92);
        }
        
        // Animation organique basée sur le temps
        const freq1 = Math.sin(time * (4 + i * 0.3) + i * 0.7) * 0.5 + 0.5;
        const freq2 = Math.cos(time * (6 + i * 0.4) + i * 1.2) * 0.5 + 0.5;
        const freq3 = Math.sin(time * (8 + i * 0.2) + i * 0.5) * 0.5 + 0.5;
        const target = 0.15 + freq1 * 0.35 + freq2 * 0.3 + freq3 * 0.2;
        
        const speed = 0.2;
        return currentValue + (target - currentValue) * speed;
      });
      
      // Mise à jour DOM directe (pas de re-render React)
      barElements.forEach((bar, i) => {
        if (bar) {
          bar.style.height = `${Math.max(8, barsRef.current[i] * 100)}%`;
        }
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  
  // Écouter les événements audio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handlePlay = () => { isPlayingRef.current = true; };
    const handlePause = () => { isPlayingRef.current = false; };
    
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('playing', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handlePause);
    
    if (!audio.paused && !audio.ended) {
      isPlayingRef.current = true;
    }
    
    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('playing', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handlePause);
    };
  }, [audioRef]);
  
  return (
    <div ref={containerRef} className="flex items-end justify-center gap-1 h-24 mb-8">
      {Array.from({ length: 20 }).map((_, i) => (
        <div
          key={i}
          className="visualizer-bar w-2 bg-gradient-to-t from-primary-500 to-secondary-500 rounded-full"
          style={{ height: '8%' }}
        />
      ))}
    </div>
  );
}

export default function GamePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { player, isAuthenticated } = useAuthStore();
  const { 
    currentRoom,
    currentRound,
    roundResult,
    gameResult,
    scores,
    myAnswer,
    submitAnswer,
    submitQuizAnswer,
    resetGame,
    isWaitingForPlayers,
    playedRounds,
    playerAnswers,
    // Mode de jeu
    gameMode,
    modeConfig,
    currentQuestion,
    quizAnswered: _quizAnswered,
    playerLives: _playerLives,
    isEliminated,
    teams: _teams,
    teamScores: _teamScores,
  } = useSocketStore();
  
  const [answer, setAnswer] = useState('');
  const [timeLeft, setTimeLeft] = useState(30);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [progress, setProgress] = useState(100);
  const [shakeInput, setShakeInput] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);
  const [prevTitleFound, setPrevTitleFound] = useState(false);
  const [prevArtistFound, setPrevArtistFound] = useState(false);
  const [showPowerUps, setShowPowerUps] = useState(false);  // Toggle power-ups
  
  // Power-up effects (frozen state)
  const { isFrozen } = usePowerUpEffects();
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const progressIntervalRef = useRef<number | null>(null);
  const roundIdRef = useRef<number | null>(null);
  
  const { volume, sliderValue, setVolume, isMuted, toggleMute } = useVolume();
  const { playSuccess, playError, playCountdown } = useSoundEffects(volume, isMuted);
  
  // Normalisation audio avec compresseur dynamique
  useAudioNormalization(audioRef as React.RefObject<HTMLAudioElement>, volume, isMuted);
  
  // Refs pour avoir accès aux valeurs actuelles dans les callbacks
  const volumeRef = useRef(volume);
  const isMutedRef = useRef(isMuted);
  
  // Mettre à jour les refs quand les valeurs changent
  useEffect(() => {
    volumeRef.current = volume;
    isMutedRef.current = isMuted;
  }, [volume, isMuted]);
  
  // Appliquer le volume à l'audio quand il change
  useEffect(() => {
    if (audioRef.current) {
      const newVolume = isMuted ? 0 : volume;
      audioRef.current.volume = newVolume;
      console.log('Volume appliqué:', newVolume);
    }
  }, [volume, isMuted]);
  
  // Countdown avant le round
  useEffect(() => {
    if (currentRound && currentRound.round !== roundIdRef.current) {
      console.log('🎵 Nouveau round reçu:', {
        round: currentRound.round,
        previewUrl: currentRound.previewUrl ? 'présent' : 'ABSENT',
        duration: currentRound.duration
      });
      
      roundIdRef.current = currentRound.round;
      
      // Reset des états pour le nouveau round
      setLastAnswerCorrect(null);
      setPrevTitleFound(false);
      setPrevArtistFound(false);
      
      // Démarrer le countdown
      setShowCountdown(true);
      setCountdown(3);
      setProgress(100);
      
      // Arrêter l'audio pendant le countdown
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      let countdownValue = 3;
      playCountdown();
      
      const countdownInterval = setInterval(() => {
        countdownValue--;
        setCountdown(countdownValue);
        
        if (countdownValue > 0) {
          playCountdown();
        }
        
        if (countdownValue <= 0) {
          clearInterval(countdownInterval);
          setShowCountdown(false);
          // Démarrer l'audio après le countdown
          console.log('🔊 Tentative lecture audio:', {
            hasAudioRef: !!audioRef.current,
            previewUrl: currentRound.previewUrl
          });
          // Don't play audio in deaf mode — the YouTube iframe handles it
          if (!currentRound.isDeafMode && audioRef.current && currentRound.previewUrl) {
            audioRef.current.src = currentRound.previewUrl;
            audioRef.current.loop = true; // Loop preview until round ends
            // Utiliser les refs pour avoir les valeurs actuelles
            const currentVolume = isMutedRef.current ? 0 : volumeRef.current;
            audioRef.current.volume = currentVolume;
            audioRef.current.play().catch(err => console.error('Erreur lecture audio:', err));
          }
        }
      }, 1000);
      
      return () => clearInterval(countdownInterval);
    }
  }, [currentRound?.round, playCountdown]);
  
  // Timer principal
  useEffect(() => {
    if (currentRound && !showCountdown) {
      setTimeLeft(currentRound.duration);
      setAnswer('');
      inputRef.current?.focus();
      
      // Timer pour le temps restant
      const interval = setInterval(() => {
        setTimeLeft((prev) => Math.max(0, prev - 1));
      }, 1000);
      
      // Timer pour la barre de progression (plus fluide)
      const startTime = Date.now();
      const duration = currentRound.duration * 1000;
      
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      
      progressIntervalRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
        setProgress(remaining);
      }, 50);
      
      return () => {
        clearInterval(interval);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };
    }
  }, [currentRound, showCountdown]);
  
  // Resume AudioContext on first user interaction (fixes autoplay for non-host)
  useEffect(() => {
    const resumeAudio = () => {
      if (audioContext && audioContext.state === 'suspended') {
        audioContext.resume().then(() => console.log('🔊 AudioContext resumed by user interaction'));
      }
    };
    document.addEventListener('click', resumeAudio, { once: false });
    document.addEventListener('keydown', resumeAudio, { once: false });
    return () => {
      document.removeEventListener('click', resumeAudio);
      document.removeEventListener('keydown', resumeAudio);
    };
  }, []);
  
  // Fallback: if roundResult is shown but no next round arrives within 12s, something went wrong
  useEffect(() => {
    if (roundResult && !gameResult) {
      const fallbackTimer = setTimeout(() => {
        console.warn('⚠️ Round end stuck - no next round received after 12s');
        // Force the game to check state - if we're still on roundResult, the server may have lost track
      }, 12000);
      return () => clearTimeout(fallbackTimer);
    }
  }, [roundResult, gameResult]);
  
  // Confetti à la fin de la partie
  useEffect(() => {
    if (gameResult && gameResult.winner.playerId === player?.id) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }, [gameResult, player]);
  
  // Effet sonore quand on trouve une réponse
  useEffect(() => {
    if (myAnswer) {
      // Vérifier si on vient de trouver quelque chose de nouveau
      if (myAnswer.titleFound && !prevTitleFound) {
        playSuccess();
        setLastAnswerCorrect(true);
        setPrevTitleFound(true);
        
        // Confetti léger
        confetti({
          particleCount: 30,
          spread: 50,
          origin: { y: 0.7 },
          colors: ['#6366f1', '#a855f7', '#22c55e']
        });
      }
      
      if (myAnswer.artistFound && !prevArtistFound) {
        playSuccess();
        setLastAnswerCorrect(true);
        setPrevArtistFound(true);
        
        // Confetti léger
        confetti({
          particleCount: 30,
          spread: 50,
          origin: { y: 0.7 },
          colors: ['#6366f1', '#a855f7', '#22c55e']
        });
      }
    }
  }, [myAnswer?.titleFound, myAnswer?.artistFound, prevTitleFound, prevArtistFound, playSuccess]);
  
  // Retour au lobby si pas de room
  useEffect(() => {
    if (!currentRoom && !gameResult) {
      navigate('/', { replace: true });
    }
  }, [currentRoom, gameResult, navigate]);
  
  const handleSubmitAnswer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!answer.trim()) return;
    
    // Si gelé, bloquer
    if (isFrozen) return;
    
    // Si déjà tout trouvé, bloquer
    if (myAnswer?.titleFound && myAnswer?.artistFound) return;
    
    const currentTitleFound = myAnswer?.titleFound || false;
    const currentArtistFound = myAnswer?.artistFound || false;
    
    submitAnswer(answer.trim());
    setAnswer('');
    
    // Vérifier après un court délai si la réponse était correcte
    setTimeout(() => {
      const newAnswer = useSocketStore.getState().myAnswer;
      const foundTitle = newAnswer?.titleFound && !currentTitleFound;
      const foundArtist = newAnswer?.artistFound && !currentArtistFound;
      
      if (!foundTitle && !foundArtist) {
        // Mauvaise réponse - animation shake et son
        setShakeInput(true);
        setLastAnswerCorrect(false);
        playError();
        setTimeout(() => {
          setShakeInput(false);
          setLastAnswerCorrect(null);
        }, 500);
      }
    }, 150);
  };
  
  const handleBackToLobby = () => {
    resetGame();
    if (code) {
      navigate(`/room/${code}`, { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };
  
  // Persistent audio element - rendered before all conditional returns so it's always in the DOM
  const audioElement = (
    <audio 
      ref={audioRef}
      crossOrigin="anonymous"
      preload="auto"
      onLoadedData={() => {
        if (audioRef.current) {
          audioRef.current.volume = isMuted ? 0 : volume;
        }
      }}
      onPlay={() => {
        if (audioRef.current) {
          audioRef.current.volume = isMuted ? 0 : volume;
        }
      }}
      onError={(e) => {
        console.error('❌ Audio error:', (e.target as HTMLAudioElement).error);
      }}
    />
  );
  
  // Auth guard
  if (!isAuthenticated || !player) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        {audioElement}
        <h1 className="text-2xl font-bold mb-4">Connexion requise</h1>
        <p className="text-dark-400 mb-6">Vous devez être connecté pour accéder à cette page.</p>
        <Link to="/" className="btn-primary">Retour à l'accueil</Link>
      </div>
    );
  }

  // Écran de fin de partie
  if (gameResult) {
    const myScore = scores.find(s => s.playerId === player?.id);
    const myRank = scores.findIndex(s => s.playerId === player?.id) + 1;
    
    return (
      <div className="max-w-4xl mx-auto text-center">
        {audioElement}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="font-display text-4xl font-bold mb-2">
            🎉 Partie terminée !
          </h1>
          
          {/* Podium */}
          <div className="flex justify-center items-end gap-4 my-12">
            {/* 2ème place */}
            {scores[1] && (
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {scores[1].playerAvatar ? (
                  <img 
                    src={scores[1].playerAvatar} 
                    alt="" 
                    className="w-20 h-20 mx-auto mb-2 rounded-full border-4 border-gray-400 shadow-lg object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 mx-auto mb-2 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center text-2xl font-bold shadow-lg">
                    2
                  </div>
                )}
                <p className="font-semibold">{scores[1].playerName}</p>
                <p className="text-primary-400">{scores[1].points} pts</p>
              </motion.div>
            )}
            
            {/* 1ère place */}
            {scores[0] && (
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 50, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.5, type: 'spring' }}
              >
                <Trophy className="w-12 h-12 text-yellow-500 mx-auto mb-2 animate-bounce" />
                {scores[0].playerAvatar ? (
                  <img 
                    src={scores[0].playerAvatar} 
                    alt="" 
                    className="w-24 h-24 mx-auto mb-2 rounded-full border-4 border-yellow-500 shadow-glow object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 mx-auto mb-2 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-3xl font-bold shadow-glow">
                    1
                  </div>
                )}
                <p className="font-bold text-lg">{scores[0].playerName}</p>
                <p className="text-primary-400 font-semibold">{scores[0].points} pts</p>
              </motion.div>
            )}
            
            {/* 3ème place */}
            {scores[2] && (
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                {scores[2].playerAvatar ? (
                  <img 
                    src={scores[2].playerAvatar} 
                    alt="" 
                    className="w-20 h-20 mx-auto mb-2 rounded-full border-4 border-orange-500 shadow-lg object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 mx-auto mb-2 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-2xl font-bold shadow-lg">
                    3
                  </div>
                )}
                <p className="font-semibold">{scores[2].playerName}</p>
                <p className="text-primary-400">{scores[2].points} pts</p>
              </motion.div>
            )}
          </div>
          
          {/* Mon score */}
          {myScore && myRank > 3 && (
            <motion.div 
              className="card inline-block mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <p className="text-dark-400 mb-1">Votre classement</p>
              <p className="text-2xl font-bold">
                {myRank}ème avec {myScore.points} points
              </p>
            </motion.div>
          )}
          
          {/* Tous les scores */}
          <motion.div 
            className="card max-w-md mx-auto mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className="font-semibold mb-4 flex items-center justify-center gap-2">
              <Users className="w-5 h-5 text-primary-400" />
              Classement complet
            </h2>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {scores.map((score, index) => (
                <motion.div 
                  key={score.playerId}
                  className={`flex items-center justify-between p-2 rounded-lg ${
                    score.playerId === player?.id ? 'bg-primary-500/20 border border-primary-500' : 'bg-white/5'
                  }`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 + index * 0.05 }}
                >
                  <span className="flex items-center gap-2">
                    {/* Rang */}
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-500' : 'bg-dark-700'
                    }`}>
                      {index + 1}
                    </span>
                    {/* Avatar */}
                    {score.playerAvatar ? (
                      <img 
                        src={score.playerAvatar} 
                        alt="" 
                        className="w-6 h-6 rounded-full flex-shrink-0 object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full flex-shrink-0 bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-xs font-bold">
                        {score.playerName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span>{score.playerName}</span>
                  </span>
                  <span className="font-semibold text-primary-400">{score.points}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          <motion.button 
            onClick={handleBackToLobby} 
            className="btn-primary"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Retour au lobby
          </motion.button>
        </motion.div>
      </div>
    );
  }
  
  // Écran de résultat de round
  if (roundResult) {
    return (
      <div className="max-w-4xl mx-auto text-center">
        {audioElement}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="text-dark-400 mb-2">
            Round {roundResult.round} terminé
          </p>
          
          {/* Réponse révélée */}
          <motion.div 
            className="card inline-block mb-8"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
          >
            {roundResult.answer.albumCover && (
              <motion.img 
                src={roundResult.answer.albumCover} 
                alt="Album" 
                className="w-32 h-32 rounded-lg mx-auto mb-4 shadow-lg"
                initial={{ rotateY: 180, opacity: 0 }}
                animate={{ rotateY: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
              />
            )}
            {roundResult?.answer?.isDeafMode && roundResult?.answer?.youtubeVideoId && (
              <div className="w-full aspect-video max-w-md mx-auto rounded-xl overflow-hidden mb-4">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${roundResult.answer.youtubeVideoId}?autoplay=1&start=${roundResult.answer.videoStartTime || 30}&controls=0`}
                  className="w-full h-full"
                  allow="autoplay; encrypted-media"
                />
              </div>
            )}
            <h2 className="font-display text-2xl font-bold mb-1">
              {roundResult.answer.title}
            </h2>
            <p className="text-primary-400">{roundResult.answer.artist}</p>
          </motion.div>
          
          {/* Scores du round */}
          <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {roundResult.scores.slice(0, 6).map((score, index) => (
              <motion.div 
                key={score.playerId}
                className={`card-hover p-4 ${score.playerId === player?.id ? 'border-primary-500 border-2' : ''}`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' : 
                      index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
                      index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600' :
                      'bg-gradient-to-br from-primary-500 to-secondary-500'
                    }`}>
                      {index + 1}
                    </span>
                    <span>{score.playerName}</span>
                  </span>
                  <span className="font-bold text-primary-400">{score.points}</span>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.p 
            className="text-dark-400 mt-8"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            Prochain round dans quelques secondes...
          </motion.p>
        </motion.div>
      </div>
    );
  }
  
  // Écran d'attente des joueurs
  if (isWaitingForPlayers) {
    return (
      <div className="max-w-4xl mx-auto text-center flex flex-col items-center justify-center min-h-[60vh]">
        {audioElement}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-6xl mb-8"
          >
            🎵
          </motion.div>
          
          <h1 className="font-display text-3xl font-bold mb-4 bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
            Préparation de la partie...
          </h1>
          
          <p className="text-dark-400 mb-8">
            En attente que tous les joueurs soient prêts
          </p>
          
          {/* Liste des joueurs */}
          <div className="card inline-block min-w-[300px] mb-8">
            <h2 className="font-semibold mb-4 flex items-center justify-center gap-2">
              <Users className="w-5 h-5 text-primary-400" />
              Joueurs ({currentRoom?.players.length || 0})
            </h2>
            <div className="space-y-2">
              {currentRoom?.players.map((p, index) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center gap-3 p-2 rounded-lg ${
                    p.id === player?.id ? 'bg-primary-500/20 border border-primary-500' : 'bg-white/5'
                  }`}
                >
                  {p.avatar ? (
                    <img src={p.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-bold">
                      {p.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="font-medium">{p.name}</span>
                  {p.id === currentRoom?.hostId && (
                    <span className="ml-auto text-xs bg-primary-500/30 px-2 py-1 rounded">Host</span>
                  )}
                  <motion.div
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.2 }}
                    className="w-2 h-2 rounded-full bg-green-500 ml-auto"
                  />
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Animation de chargement */}
          <div className="flex justify-center gap-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-3 h-3 rounded-full bg-primary-500"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}
          </div>
          
          <motion.p
            className="text-dark-500 text-sm mt-4"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Le jeu commence dans quelques instants...
          </motion.p>
        </motion.div>
      </div>
    );
  }
  
  // Écran de countdown
  if (showCountdown) {
    return (
      <div className="max-w-4xl mx-auto text-center flex items-center justify-center min-h-[60vh]">
        {audioElement}
        <AnimatePresence mode="wait">
          <motion.div
            key={countdown}
            initial={{ scale: 2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="text-center"
          >
            {countdown > 0 ? (
              <>
                <div className="text-8xl font-display font-bold bg-gradient-to-br from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                  {countdown}
                </div>
                <p className="text-dark-400 mt-4 text-lg">Préparez-vous...</p>
              </>
            ) : (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="text-6xl"
              >
                🎵
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }
  
  // Écran de jeu principal
  return (
    <div className="max-w-4xl mx-auto">
      {audioElement}
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <p className="text-dark-400">
              Round {currentRound?.round || 1} / {currentRound?.totalRounds || '?'}
            </p>
            {gameMode && gameMode !== 'classic' && (
              <GameModeBadge mode={gameMode} size="sm" />
            )}
          </div>
          <h1 className="font-display text-2xl font-bold">{currentRoom?.name}</h1>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Contrôle du volume */}
          <div className="flex items-center gap-2">
            <button 
              onClick={toggleMute}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={isMuted ? 0 : sliderValue}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-24 h-2 bg-dark-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
              title={`Volume: ${Math.round(sliderValue)}%`}
            />
          </div>
          
          {/* Timer */}
          <div className={`flex items-center gap-2 text-lg font-mono ${timeLeft <= 10 ? 'text-error animate-pulse' : 'text-dark-300'}`}>
            <Clock className="w-5 h-5" />
            {timeLeft}s
          </div>
          
          {/* Bouton Power-ups */}
          <motion.button
            onClick={() => setShowPowerUps(true)}
            className="btn-secondary px-3 py-2 flex items-center gap-2"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={isFrozen}
          >
            <Sparkles className="w-5 h-5 text-yellow-400" />
            <span className="hidden sm:inline">Power-ups</span>
          </motion.button>
        </div>
      </div>
      
      {/* Barre de progression du temps */}
      <div className="h-2 bg-dark-700 rounded-full mb-6 overflow-hidden">
        <motion.div 
          className={`h-full rounded-full transition-colors duration-300 ${
            progress > 50 ? 'bg-gradient-to-r from-green-500 to-green-400' :
            progress > 25 ? 'bg-gradient-to-r from-yellow-500 to-orange-400' :
            'bg-gradient-to-r from-red-500 to-red-400'
          }`}
          style={{ width: `${progress}%` }}
          initial={{ width: '100%' }}
        />
      </div>
      
      {/* Zone de jeu principale */}
      <motion.div 
        className={`card text-center py-12 mb-6 transition-all duration-300 ${
          lastAnswerCorrect === false ? 'border-red-500 bg-red-500/5' : 
          lastAnswerCorrect === true ? 'border-green-500 bg-green-500/5' : ''
        } ${isEliminated ? 'opacity-50' : ''}`}
        animate={shakeInput ? { x: [-10, 10, -10, 10, 0] } : {}}
        transition={{ duration: 0.4 }}
      >
        {/* Battle Royale: Afficher les vies */}
        {modeConfig?.hasLives && (
          <BattleRoyaleStatus />
        )}
        
        {/* Teams: Afficher les scores d'équipe */}
        {modeConfig?.teamBased && (
          <TeamScoreDisplay />
        )}
        
        {/* Deaf Test: YouTube video embed (muted) OR Audio Visualizer */}
        {currentRound?.isDeafMode && currentRound?.youtubeVideoId ? (
          <div className="relative w-full aspect-video max-w-2xl mx-auto rounded-xl overflow-hidden bg-dark-900 mb-6">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${currentRound.youtubeVideoId}?autoplay=1&mute=1&start=${currentRound.videoStartTime || 30}&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&disablekb=1&fs=0&origin=${encodeURIComponent(window.location.origin)}`}
              className="w-full h-full"
              allow="autoplay; encrypted-media"
              style={{ pointerEvents: 'none' }}
            />
            <div className="absolute top-3 right-3 bg-black/60 rounded-full px-3 py-1 flex items-center gap-2 text-white text-xs">
              <VolumeX className="w-4 h-4" />
              DEAF TEST
            </div>
          </div>
        ) : (
          <AudioVisualizer audioRef={audioRef as React.RefObject<HTMLAudioElement>} />
        )}
        
        {/* Message d'élimination */}
        {isEliminated ? (
          <div className="text-center py-8">
            <Skull className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-400 mb-2">Éliminé !</h2>
            <p className="text-dark-400">Attendez la fin de la partie...</p>
          </div>
        ) : modeConfig?.qcmMode && currentQuestion ? (
          /* Mode Quiz: Afficher les choix multiples */
          <QuizMode onAnswer={submitQuizAnswer} />
        ) : (
          /* Mode classique: Input texte */
          <>
            <p className="text-dark-400 mb-6">
              🎵 Écoutez et devinez la musique !
            </p>
            
            {/* Indicateurs de réponse */}
            <div className="flex justify-center gap-4 mb-6">
              <motion.div 
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  myAnswer?.titleFound 
                    ? 'bg-success/20 text-success border border-success' 
                    : 'bg-white/5 text-dark-400'
                }`}
                animate={myAnswer?.titleFound ? { scale: [1, 1.1, 1] } : {}}
              >
                {myAnswer?.titleFound ? <Check className="w-5 h-5" /> : <Music className="w-5 h-5" />}
                <span>Titre</span>
              </motion.div>
              <motion.div 
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  myAnswer?.artistFound 
                    ? 'bg-success/20 text-success border border-success' 
                    : 'bg-white/5 text-dark-400'
                }`}
                animate={myAnswer?.artistFound ? { scale: [1, 1.1, 1] } : {}}
              >
                {myAnswer?.artistFound ? <Check className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                <span>Artiste</span>
              </motion.div>
            </div>
            
            {/* Input réponse */}
            <form onSubmit={handleSubmitAnswer} className="max-w-md mx-auto">
              <motion.div 
                className="flex gap-2"
                animate={shakeInput ? { x: [-5, 5, -5, 5, 0] } : {}}
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder={
                    myAnswer?.titleFound && myAnswer?.artistFound 
                      ? "🎉 Tout trouvé !" 
                      : myAnswer?.titleFound 
                        ? "Cherchez l'artiste..." 
                        : myAnswer?.artistFound 
                          ? "Cherchez le titre..." 
                          : "Titre ou artiste..."
                  }
                  className={`input-field flex-1 text-center text-lg transition-all ${
                    shakeInput ? 'border-red-500 bg-red-500/10' : ''
                  } ${
                    myAnswer?.titleFound && myAnswer?.artistFound ? 'bg-green-500/10 border-green-500' : ''
                  }`}
                  disabled={myAnswer?.titleFound && myAnswer?.artistFound}
                  autoComplete="off"
                />
                <motion.button
                  type="submit"
                  className="btn-primary px-6"
                  disabled={!answer.trim() || (myAnswer?.titleFound && myAnswer?.artistFound)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Send className="w-5 h-5" />
                </motion.button>
              </motion.div>
            </form>
            
            {myAnswer?.titleFound && myAnswer?.artistFound && (
              <motion.p 
                className="text-success mt-4 font-semibold"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                >
                  ✨
                </motion.span>
                {' '}Bravo ! Vous avez tout trouvé !{' '}
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity, delay: 0.25 }}
                >
                  ✨
                </motion.span>
              </motion.p>
            )}
          </>
        )}
      </motion.div>
      
      {/* Scores et Historique */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Scores en direct */}
        <motion.div 
          className="card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary-400" />
            {scores.length > 0 ? 'Classement' : 'Joueurs'}
          </h2>
          <div className="space-y-2">
            {/* Afficher les scores si disponibles, sinon la liste des joueurs */}
            {scores.length > 0 ? (
              scores.map((score, index) => (
              <motion.div 
                key={score.playerId}
                className={`flex items-center gap-3 p-2 rounded-lg text-sm transition-all ${
                  score.playerId === player?.id 
                    ? 'bg-primary-500/20 border border-primary-500' 
                    : 'bg-white/5'
                }`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                {/* Rang */}
                <span className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold ${
                  index === 0 ? 'bg-yellow-500' : 
                  index === 1 ? 'bg-gray-400' : 
                  index === 2 ? 'bg-orange-500' : 
                  'bg-dark-700'
                }`}>
                  {index + 1}
                </span>
                
                {/* Avatar */}
                {score.playerAvatar ? (
                  <img 
                    src={score.playerAvatar} 
                    alt="" 
                    className="w-8 h-8 rounded-full flex-shrink-0 object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full flex-shrink-0 bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-xs font-bold">
                    {score.playerName.charAt(0).toUpperCase()}
                  </div>
                )}
                
                {/* Nom et combo */}
                <div className="flex-1 min-w-0">
                  <span className="truncate block font-medium">{score.playerName}</span>
                  {score.combo > 1 && (
                    <span className="text-xs text-secondary-400 flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      Combo x{score.combo}
                    </span>
                  )}
                </div>
                
                {/* Indicateurs titre/artiste trouvés pour le round en cours */}
                {currentRound && (() => {
                  const playerAnswer = playerAnswers.find(p => p.playerId === score.playerId);
                  if (playerAnswer) {
                    return (
                      <div className="flex gap-1 mr-2">
                        {playerAnswer.titleFound && (
                          <span className="w-5 h-5 rounded bg-green-500/30 flex items-center justify-center text-xs" title="Titre trouvé">
                            🎵
                          </span>
                        )}
                        {playerAnswer.artistFound && (
                          <span className="w-5 h-5 rounded bg-blue-500/30 flex items-center justify-center text-xs" title="Artiste trouvé">
                            🎤
                          </span>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {/* Points */}
                <span className="font-bold text-primary-400 text-lg">{score.points}</span>
              </motion.div>
            ))
            ) : (
              /* Afficher la liste des joueurs de la room avec leurs indicateurs */
              currentRoom?.players.map((p, index) => {
                const playerAnswer = playerAnswers.find(pa => pa.playerId === p.id);
                return (
                  <motion.div 
                    key={p.id}
                    className={`flex items-center gap-3 p-2 rounded-lg text-sm transition-all ${
                      p.id === player?.id 
                        ? 'bg-primary-500/20 border border-primary-500' 
                        : 'bg-white/5'
                    }`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {/* Avatar */}
                    {p.avatar ? (
                      <img 
                        src={p.avatar} 
                        alt="" 
                        className="w-8 h-8 rounded-full flex-shrink-0 object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full flex-shrink-0 bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-xs font-bold">
                        {p.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    
                    {/* Nom */}
                    <span className="flex-1 truncate font-medium">{p.name}</span>
                    
                    {/* Indicateurs titre/artiste trouvés */}
                    {playerAnswer && (
                      <div className="flex gap-1">
                        <span 
                          className={`w-6 h-6 rounded flex items-center justify-center text-xs ${
                            playerAnswer.titleFound ? 'bg-green-500/30' : 'bg-dark-700/50'
                          }`} 
                          title="Titre"
                        >
                          🎵
                        </span>
                        <span 
                          className={`w-6 h-6 rounded flex items-center justify-center text-xs ${
                            playerAnswer.artistFound ? 'bg-blue-500/30' : 'bg-dark-700/50'
                          }`} 
                          title="Artiste"
                        >
                          🎤
                        </span>
                      </div>
                    )}
                    {!playerAnswer && (
                      <div className="flex gap-1">
                        <span className="w-6 h-6 rounded bg-dark-700/50 flex items-center justify-center text-xs opacity-30">🎵</span>
                        <span className="w-6 h-6 rounded bg-dark-700/50 flex items-center justify-center text-xs opacity-30">🎤</span>
                      </div>
                    )}
                  </motion.div>
                );
              })
            )}
            
            {scores.length === 0 && (!currentRoom?.players || currentRoom.players.length === 0) && (
              <p className="text-dark-500 text-center py-4">Aucun joueur</p>
            )}
          </div>
        </motion.div>
        
        {/* Historique des chansons - Toujours visible */}
        <motion.div 
          className="card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="font-semibold mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-primary-400" />
            Chansons jouées ({playedRounds.length})
          </div>
          
          <div 
            className="space-y-2 max-h-[300px] overflow-y-auto"
          >
            {playedRounds.length === 0 ? (
              <p className="text-dark-500 text-center py-4">
                Les chansons jouées apparaîtront ici
              </p>
            ) : (
              [...playedRounds].reverse().map((round, index) => (
                <motion.div
                  key={round.round}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                >
                  {/* Numéro du round */}
                  <span className="w-6 h-6 rounded-full bg-dark-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {round.round}
                  </span>
                  
                  {/* Cover */}
                  {round.albumCover ? (
                    <img 
                      src={round.albumCover} 
                      alt="" 
                      className="w-10 h-10 rounded flex-shrink-0 object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gradient-to-br from-primary-500/30 to-secondary-500/30 flex items-center justify-center flex-shrink-0">
                      <Music className="w-5 h-5 text-primary-400" />
                    </div>
                  )}
                  
                  {/* Titre et artiste */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{round.title}</p>
                    <p className="text-sm text-dark-400 truncate">{round.artist}</p>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </motion.div>
      </div>
      
      {/* Power-ups Menu */}
      <PowerUpsMenu 
        isOpen={showPowerUps}
        onClose={() => setShowPowerUps(false)}
        players={currentRoom?.players.map(p => ({ id: p.id, name: p.name })) || []}
        myId={player?.id || ''}
      />
      
      {/* Power-up Effects (gel, notifications, indices) */}
      <PowerUpEffects />
    </div>
  );
}
